import React, { useRef } from 'react';
import { IconMessageCircle, IconBrandInstagram, IconUserPlus, IconMessageReply, IconBrandWhatsapp, IconAddressBook, IconBolt } from '@tabler/icons-react';
import { Anchor, Glow, Pill, Core, Wires, Sparks, gsap, travel, flare, blink, charge, ambience, entrance, useSceneAnimation } from '../../../../../../components/Scene';
import './styles.scss';

// Triggers on the left feed the core; each run lights its paired action on the right.
const TOP = 132;
const MID = 234;
const BOT = 336;
const WIRES = [
  { id: 'status', d: 'M180 77 V192', dashed: true },
  { id: 'comment', tone: 'warm', d: 'M96 145 V202 Q96 210 104 210 H138', joints: [[96, 145], [138, 210]] },
  { id: 'direct', tone: 'warm', d: 'M104 234 H138', joints: [[104, 234], [138, 234]] },
  { id: 'follower', tone: 'warm', d: 'M96 323 V266 Q96 258 104 258 H138', joints: [[96, 323], [138, 258]] },
  { id: 'reply', tone: 'cool', d: 'M222 210 H256 Q264 210 264 202 V145', joints: [[222, 210], [264, 145]] },
  { id: 'whatsapp', tone: 'cool', d: 'M222 234 H256', joints: [[222, 234], [256, 234]] },
  { id: 'contact', tone: 'cool', d: 'M222 258 H256 Q264 258 264 266 V323', joints: [[222, 258], [264, 323]] },
];
const SPARKS = [[150, 186], [214, 180], [168, 290], [206, 296], [130, 240]];
const FLOWS = [['comment', 'reply'], ['direct', 'whatsapp'], ['follower', 'contact']];
const STEP = 2.3;

function build(q, { still }) {
  const progress = q('[data-progress]');
  if (still) {
    gsap.set(q('[data-ring="direct"], [data-ring="whatsapp"]'), { opacity: 0.9 });
    return;
  }
  ambience(q);
  gsap.to(q('[data-status-dot]'), { opacity: 0.3, duration: 0.8, ease: 'sine.inOut', yoyo: true, repeat: -1 });
  const intro = entrance(q);
  gsap.set(progress, { scaleX: 0 });
  const loop = gsap.timeline({ repeat: -1, repeatDelay: 0.4, delay: intro.duration() - 0.2 });
  FLOWS.forEach(([from, to], i) => {
    const at = i * STEP;
    flare(loop, q, from, at);
    blink(loop, q, [`${from}-0`], at + 0.12);
    travel(loop, q, from, at + 0.15);
    blink(loop, q, [`${from}-1`], at + 0.82);
    charge(loop, q, at + 0.85);
    blink(loop, q, [`${to}-0`], at + 0.95);
    travel(loop, q, to, at + 0.98);
    blink(loop, q, [`${to}-1`], at + 1.65);
    flare(loop, q, to, at + 1.66, 0.5);
    loop.to(progress, { scaleX: (i + 1) / FLOWS.length, duration: 0.5, ease: 'power2.out' }, at + 1.66);
  });
  loop.to(progress, { opacity: 0, duration: 0.4 }, '+=0.5').set(progress, { scaleX: 0, opacity: 1 });
}

export default function AutomationsScene({ t }) {
  const stage = useRef(null);
  useSceneAnimation(stage, build);
  return <div className="studio-feature-art__stage" ref={stage} aria-hidden="true">
    <Glow x={136} y={282} tone="warm" />
    <Glow x={226} y={186} tone="cool" />
    <Wires wires={WIRES} />
    <Sparks points={SPARKS} />
    <Anchor x={180} y={64}>
      <span className="studio-feature-art__pill studio-feature-art__status is-cool" data-enter>
        <i className="studio-feature-art__status-dot" data-status-dot />{t('artFlowActive')}
        <span className="studio-feature-art__status-track"><span className="studio-feature-art__status-fill" data-progress /></span>
      </span>
    </Anchor>
    <Anchor x={96} y={TOP}><Pill id="comment" tone="warm" icon={IconMessageCircle}>{t('artNewComment')}</Pill></Anchor>
    <Anchor x={104} y={MID} edge="end"><Pill id="direct" tone="warm" icon={IconBrandInstagram}>{t('artDirect')}</Pill></Anchor>
    <Anchor x={96} y={BOT}><Pill id="follower" tone="warm" icon={IconUserPlus}>{t('artNewFollower')}</Pill></Anchor>
    <Anchor x={264} y={TOP}><Pill id="reply" icon={IconMessageReply}>{t('artReply')}</Pill></Anchor>
    <Anchor x={256} y={MID} edge="start"><Pill id="whatsapp" icon={IconBrandWhatsapp}>WhatsApp</Pill></Anchor>
    <Anchor x={264} y={BOT}><Pill id="contact" icon={IconAddressBook}>{t('artSaveContact')}</Pill></Anchor>
    <Anchor x={180} y={MID}><Core icon={IconBolt} /></Anchor>
  </div>;
}
